import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Scissors, Check, X as XIcon, ArrowRight, Clock, Sparkles, Infinity } from "lucide-react";
import PlatformLogo from "@/components/PlatformLogo";

function fmtCountdown(iso) {
  if (!iso) return "";
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return "abgelaufen";
  const d = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  if (d > 0) return `Aktion endet in ${d} Tag${d === 1 ? "" : "en"}`;
  const m = Math.floor((ms % 3600000) / 60000);
  return `Aktion endet in ${h} Std ${m} Min`;
}

const FEATURE_LABELS = {
  cms_pages: "Drag & Drop Seitenbuilder",
  theme_studio: "Theme Studio (Farben & Schriften)",
  header_footer: "Header & Footer Editor",
  blog: "Blog-Modul",
  gallery: "Galerie",
  forms: "Formular-Builder",
  seo: "SEO-Manager",
  services: "Leistungen",
  service_categories: "Service-Kategorien",
  addons: "Zusatzleistungen",
  staff: "Mitarbeiter-Verwaltung",
  staff_blocks: "Urlaub & Krankheit",
  availability: "Verfügbarkeits-Planung",
  bookings: "Buchungen & Kalender",
  ical_sync: "iCal Kalender-Sync",
  newsletter: "Newsletter",
  email_templates: "E-Mail-Vorlagen",
  email_smtp: "E-Mail SMTP",
  loyalty: "Treueprogramm",
  analytics: "Auswertungen",
  customer_portal: "Kundenbereich",
};

export default function PricingPage() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/public/plans").then(({ data }) => setPlans(data)).finally(() => setLoading(false));
  }, []);

  const goRegister = (slug) => navigate(`/register?plan=${slug}`);

  // Highlight middle plan as recommended
  const featured = plans.length === 3 ? plans[1].slug : null;

  return (
    <div className="min-h-screen" style={{ background: "var(--mv-bg)" }}>
      <header className="border-b" style={{ borderColor: "var(--mv-border)", background: "rgba(253,251,247,0.85)", backdropFilter: "blur(20px)" }}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <PlatformLogo imgClassName="h-7 w-auto object-contain">
              <Scissors className="w-5 h-5" strokeWidth={1.5} style={{ color: "var(--mv-primary)" }} />
              <span className="font-display text-lg">StudioMV</span>
            </PlatformLogo>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/login" className="mv-btn-ghost text-sm" data-testid="pricing-login-link">Anmelden</Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-center mb-14 max-w-2xl mx-auto">
          <p className="mv-label mb-3">Pakete & Preise</p>
          <h1 className="font-display text-4xl md:text-5xl mb-4">Wählen Sie Ihr Paket</h1>
          <p className="text-base" style={{ color: "var(--mv-text-muted)" }}>
            Salonsoftware + Website-CMS in einem. Starten Sie noch heute — keine Einrichtungsgebühr, monatlich kündbar.
          </p>
        </div>

        {loading ? (
          <p className="text-center" style={{ color: "var(--mv-text-muted)" }}>Lade Pakete…</p>
        ) : plans.length === 0 ? (
          <p className="text-center" style={{ color: "var(--mv-text-muted)" }}>Aktuell sind keine Pakete buchbar.</p>
        ) : (
          <div className="grid md:grid-cols-3 gap-6" data-testid="pricing-grid">
            {plans.map((p) => {
              const isFeatured = p.slug === featured;
              return (
                <div
                  key={p.id}
                  data-testid={`pricing-card-${p.slug}`}
                  className={`relative mv-card p-8 transition-shadow ${isFeatured ? "shadow-xl" : ""}`}
                  style={isFeatured ? { borderColor: "var(--mv-primary)", borderWidth: 2 } : {}}
                >
                  {isFeatured && (
                    <span
                      className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[11px] font-medium flex items-center gap-1"
                      style={{ background: "var(--mv-primary)", color: "#fff" }}
                    >
                      <Sparkles className="w-3 h-3" strokeWidth={1.8} /> Empfohlen
                    </span>
                  )}

                  <h3 className="font-display text-2xl mb-1">{p.name}</h3>
                  {p.description && (
                    <p className="text-sm mb-6 leading-relaxed" style={{ color: "var(--mv-text-muted)" }}>{p.description}</p>
                  )}

                  {p.on_sale ? (
                    <div className="mb-6">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="font-display text-4xl" style={{ color: "var(--mv-primary)" }}>
                          {p.sale_price.toFixed(2)}
                        </span>
                        <span className="text-sm" style={{ color: "var(--mv-text-muted)" }}>€ / Monat</span>
                        <span className="text-lg line-through ml-1" style={{ color: "var(--mv-text-muted)" }}>
                          {p.monthly_price.toFixed(2)} €
                        </span>
                      </div>
                      <p className="text-[11px] mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded" style={{ background: "var(--mv-primary)", color: "#fff" }}>
                        <Clock className="w-3 h-3" strokeWidth={1.8} /> {fmtCountdown(p.sale_until)}
                      </p>
                    </div>
                  ) : (
                    <div className="mb-6">
                      <span className="font-display text-4xl">{p.monthly_price.toFixed(2)}</span>
                      <span className="text-sm ml-1" style={{ color: "var(--mv-text-muted)" }}>€ / Monat</span>
                    </div>
                  )}

                  <ul className="space-y-2 mb-8">
                    {(p.features || []).slice(0, 10).map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm">
                        <Check className="w-4 h-4 flex-shrink-0 mt-0.5" strokeWidth={1.5} style={{ color: "var(--mv-secondary)" }} />
                        <span>{FEATURE_LABELS[f] || f}</span>
                      </li>
                    ))}
                    {(p.features || []).length > 10 && (
                      <li className="text-xs ml-6" style={{ color: "var(--mv-text-muted)" }}>
                        + {(p.features || []).length - 10} weitere Features
                      </li>
                    )}
                  </ul>

                  <button
                    onClick={() => goRegister(p.slug)}
                    data-testid={`pricing-choose-${p.slug}`}
                    className={isFeatured ? "mv-btn w-full justify-center" : "mv-btn-ghost w-full justify-center"}
                  >
                    {p.name} wählen <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <p className="text-center text-xs mt-12" style={{ color: "var(--mv-text-muted)" }}>
          Schon registriert? <Link to="/login" className="underline" style={{ color: "var(--mv-primary)" }}>Hier anmelden</Link>
        </p>

        {plans.length > 0 && (
          <section className="mt-20" data-testid="pricing-comparison-table">
            <div className="text-center mb-8">
              <p className="mv-label mb-2">Direkter Vergleich</p>
              <h2 className="font-display text-3xl md:text-4xl">Alle Funktionen im Überblick</h2>
              <p className="text-sm mt-2" style={{ color: "var(--mv-text-muted)" }}>
                Welches Paket bietet was? Hier siehst du jede Funktion im direkten Vergleich.
              </p>
            </div>

            <div className="overflow-x-auto -mx-6 md:mx-0">
              <table className="w-full text-sm border-collapse min-w-[600px]">
                <thead>
                  <tr>
                    <th className="text-left p-4 font-medium" style={{ color: "var(--mv-text-muted)" }}>Funktion</th>
                    {plans.map((p) => {
                      const isFeatured = featured === p.slug;
                      return (
                        <th
                          key={p.id}
                          className="text-center p-4 align-bottom"
                          style={{
                            background: isFeatured ? "rgba(20,184,166,0.06)" : "transparent",
                            borderTopLeftRadius: isFeatured ? 12 : 0,
                            borderTopRightRadius: isFeatured ? 12 : 0,
                          }}
                        >
                          <div className="font-display text-lg mb-1">{p.name}</div>
                          <div className="text-xs" style={{ color: "var(--mv-text-muted)" }}>
                            {p.monthly_price === 0 ? "Kostenlos" : `${p.monthly_price.toFixed(0)} € / Monat`}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {/* Plan limits row */}
                  <tr className="border-t" style={{ borderColor: "var(--mv-border)" }}>
                    <td className="p-4 font-medium">Max. Mitarbeitende</td>
                    {plans.map((p) => {
                      const lim = p.limits?.staff_limit;
                      const unlimited = lim === undefined || lim === null || lim < 0;
                      return (
                        <td key={p.id} className="p-4 text-center" style={{ background: featured === p.slug ? "rgba(20,184,166,0.04)" : "transparent" }}>
                          {unlimited ? (
                            <span className="inline-flex items-center gap-1 text-sm">
                              <Infinity className="w-4 h-4" strokeWidth={1.5} /> unbegrenzt
                            </span>
                          ) : (
                            <span className="text-sm font-medium">{lim}</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>

                  {/* Features rows */}
                  {Object.entries(FEATURE_LABELS).map(([key, label]) => (
                    <tr key={key} className="border-t" style={{ borderColor: "var(--mv-border)" }}>
                      <td className="p-4">{label}</td>
                      {plans.map((p) => {
                        const has = (p.features || []).includes(key);
                        const isFeat = featured === p.slug;
                        return (
                          <td
                            key={p.id}
                            className="p-4 text-center"
                            style={{ background: isFeat ? "rgba(20,184,166,0.04)" : "transparent" }}
                          >
                            {has ? (
                              <Check className="w-4 h-4 mx-auto" strokeWidth={2} style={{ color: "var(--mv-primary)" }} />
                            ) : (
                              <XIcon className="w-4 h-4 mx-auto opacity-30" strokeWidth={1.5} />
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}

                  {/* CTA footer row */}
                  <tr className="border-t" style={{ borderColor: "var(--mv-border)" }}>
                    <td className="p-4"></td>
                    {plans.map((p) => {
                      const isFeat = featured === p.slug;
                      return (
                        <td key={p.id} className="p-4 text-center" style={{
                          background: isFeat ? "rgba(20,184,166,0.04)" : "transparent",
                          borderBottomLeftRadius: isFeat ? 12 : 0,
                          borderBottomRightRadius: isFeat ? 12 : 0,
                        }}>
                          <button
                            onClick={() => goRegister(p.slug)}
                            data-testid={`pricing-compare-choose-${p.slug}`}
                            className={isFeat ? "mv-btn text-xs" : "mv-btn-ghost text-xs"}
                          >
                            Wählen
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
